<template>
  <div
    class="dashboard-stage"
    :class="{ 'dashboard-stage--mobile': isMobile }"
    :style="dashboardStageStyle"
  >
    <!-- Desktop -->
    <template v-if="!isMobile">
      <div v-for="(w, i) in widgets" :key="`desktop-${i}-${w.id}`">
        <WidgetController :widget="w" />
      </div>

      <transition name="orbit-fade">
        <div
          v-if="$store.state.addMode"
          class="orbit-panel"
          @wheel.prevent="rotateOrbit"
          @mousemove="onPanelMouseMove"
          @mouseleave="onPanelMouseLeave"
        >
          <div class="orbit-glow"></div>

          <transition name="tooltip-pop">
            <div
              v-if="hoveredIndex !== -1 && hoverTooltip && tooltipVisible"
              class="orbit-hover-tooltip"
              :style="hoverTooltipStyle"
            >
              {{ hoverTooltip }}
            </div>
          </transition>

          <button
            v-for="(w, i) in widgettype"
            :key="w.name"
            class="orbit-item"
            :class="{ 'orbit-item--active': hoveredIndex === i }"
            :style="orbitStyle(i)"
            @mouseenter="onItemEnter(i, $event)"
            @mouseleave="onItemLeave"
            @mousemove="onItemMove($event)"
            @click="addWidget(w.name)"
          >
            <v-icon :size="hoveredIndex === i ? 38 : 28">
              {{ w.icon }}
            </v-icon>
          </button>
        </div>
      </transition>
    </template>

    <!-- Mobile -->
    <template v-else>
      <div class="mobile-widget-list">
        <v-card
          v-for="w in mobileWidgetsSorted"
          :key="`mobile-${w.id}`"
          class="mobile-widget-card"
          elevation="2"
        >
          <div class="mobile-widget-card__header">
            <div class="mobile-widget-card__title">{{ w.name }}</div>
            <div class="mobile-widget-card__subtitle">
              <span v-if="w.timestamp">
                {{ new Date(w.timestamp).toLocaleDateString("th-TH") }}
                {{ new Date(w.timestamp).toLocaleTimeString("th-TH") }}
              </span>
            </div>
          </div>

          <div class="mobile-widget-card__content">
            <component :is="w.type" :widget="w" />
          </div>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import WidgetController from "../components/WidgetController.vue";

import WeatherStation from "../widgets/WeatherStation.vue";
import Lamp from "../widgets/Lamp.vue";
import ToggleSwitch from "../widgets/ToggleSwitch.vue";
import Gauge from "../widgets/ApexGauge.vue";
import Chart from "../widgets/AreaChart1.vue";
import TextInput from "../widgets/TextInput.vue";
import SilderHorizontal from "../widgets/Silder.vue";
import TimeInput from "../widgets/TimeInput.vue";
import Step from "../widgets/StepInput.vue";
import ValueDisplay from "../widgets/ValueDisplay.vue";

export default {
  components: {
    WidgetController,
    WeatherStation,
    Lamp,
    ToggleSwitch,
    Gauge,
    Chart,
    TextInput,
    SilderHorizontal,
    TimeInput,
    Step,
    ValueDisplay,
  },

  computed: {
    ...mapState(["widgettype", "widgets", "pageId"]),

    isMobile() {
      return this.$store.state.webtype !== "PC";
    },

    orbitStep() {
      return this.widgettype.length ? 360 / this.widgettype.length : 0;
    },

    hoverTooltip() {
      if (this.hoveredIndex === -1) return "";
      return this.widgettype[this.hoveredIndex]
        ? this.widgettype[this.hoveredIndex].name
        : "";
    },

    hoverTooltipStyle() {
      return {
        left: `${this.tooltipRenderX}px`,
        top: `${this.tooltipRenderY}px`,
        transform: "translate(-100%, -50%)",
      };
    },

    dashboardStageStyle() {
      if (this.isMobile) {
        return {
          width: "100%",
          minHeight: `${this.windowHeight - 64}px`,
          height: "auto",
        };
      }

      return {
        width: this.windowWidth + "px",
        height: this.windowHeight - 64 + "px",
      };
    },

    mobileWidgetsSorted() {
      return this.widgets
        .filter((w) => w.show !== false && w.pageId == this.pageId)
        .slice()
        .sort((a, b) => a.id - b.id);
    },
  },

  data() {
    return {
      orbitRotation: 180,
      hoveredIndex: -1,
      panelWidth: 220,
      panelHeight: 440,
      radius: 140,
      iconSize: 76,

      tooltipVisible: false,
      tooltipMouseX: 0,
      tooltipMouseY: 0,
      tooltipTargetX: 0,
      tooltipTargetY: 0,
      tooltipRenderX: 0,
      tooltipRenderY: 0,
      tooltipAnimFrame: null,
    };
  },

  beforeDestroy() {
    if (this.tooltipAnimFrame) {
      cancelAnimationFrame(this.tooltipAnimFrame);
      this.tooltipAnimFrame = null;
    }
  },

  methods: {
    degToRad(deg) {
      return (deg * Math.PI) / 180;
    },

    normalizeDeg(deg) {
      let value = deg % 360;
      if (value < 0) value += 360;
      return value;
    },

    rotateOrbit(event) {
      const direction = event.deltaY > 0 ? 1 : -1;
      this.orbitRotation = this.normalizeDeg(
        this.orbitRotation + direction * (this.orbitStep * 0.55)
      );
    },

    getOrbitMetrics(index) {
      const angle = this.normalizeDeg(this.orbitRotation + index * this.orbitStep);
      const rad = this.degToRad(angle);

      const centerX = this.panelWidth;
      const centerY = this.panelHeight / 2;

      const x = centerX + Math.cos(rad) * this.radius - this.iconSize / 2;
      const y = centerY + Math.sin(rad) * this.radius - this.iconSize / 2;

      const visible = Math.cos(rad) <= 0;
      const depth = (1 - Math.max(-1, Math.min(1, Math.cos(rad) * -1))) / 2;

      return { angle, rad, centerX, centerY, x, y, visible, depth };
    },

    orbitStyle(index) {
      const pos = this.getOrbitMetrics(index);
      const isActive = this.hoveredIndex === index;

      const scale = isActive ? 1.22 : 0.82 + pos.depth * 0.36;
      const opacity = pos.visible ? 0.82 + pos.depth * 0.18 : 0;

      return {
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        opacity,
        transform: `translate3d(0,0,0) scale(${scale})`,
        pointerEvents: pos.visible ? "auto" : "none",
        zIndex: isActive ? 20 : Math.round(2 + pos.depth * 10),
      };
    },

    startTooltipAnimation() {
      if (this.tooltipAnimFrame) return;

      const animate = () => {
        const ease = 0.18;
        this.tooltipRenderX += (this.tooltipTargetX - this.tooltipRenderX) * ease;
        this.tooltipRenderY += (this.tooltipTargetY - this.tooltipRenderY) * ease;

        const dx = Math.abs(this.tooltipTargetX - this.tooltipRenderX);
        const dy = Math.abs(this.tooltipTargetY - this.tooltipRenderY);

        if (dx < 0.2 && dy < 0.2) {
          this.tooltipRenderX = this.tooltipTargetX;
          this.tooltipRenderY = this.tooltipTargetY;
        }

        if (this.tooltipVisible || dx > 0.2 || dy > 0.2) {
          this.tooltipAnimFrame = requestAnimationFrame(animate);
        } else {
          cancelAnimationFrame(this.tooltipAnimFrame);
          this.tooltipAnimFrame = null;
        }
      };

      this.tooltipAnimFrame = requestAnimationFrame(animate);
    },

    updateTooltipTarget(clientX, clientY) {
      const panel = this.$el.querySelector(".orbit-panel");
      const panelRect = panel ? panel.getBoundingClientRect() : null;
      if (!panelRect) return;

      this.tooltipMouseX = clientX - panelRect.left;
      this.tooltipMouseY = clientY - panelRect.top;

      this.tooltipTargetX = this.tooltipMouseX - 14;
      this.tooltipTargetY = this.tooltipMouseY;

      this.startTooltipAnimation();
    },

    onItemEnter(index, event) {
      this.hoveredIndex = index;
      this.tooltipVisible = true;
      this.updateTooltipTarget(event.clientX, event.clientY);

      if (this.tooltipRenderX === 0 && this.tooltipRenderY === 0) {
        this.tooltipRenderX = this.tooltipTargetX;
        this.tooltipRenderY = this.tooltipTargetY;
      }
    },

    onItemMove(event) {
      if (this.hoveredIndex === -1) return;
      this.updateTooltipTarget(event.clientX, event.clientY);
    },

    onItemLeave() {
      this.hoveredIndex = -1;
      this.tooltipVisible = false;
    },

    onPanelMouseMove(event) {
      if (this.hoveredIndex === -1) return;
      this.updateTooltipTarget(event.clientX, event.clientY);
    },

    onPanelMouseLeave() {
      this.hoveredIndex = -1;
      this.tooltipVisible = false;
    },

    nextWidgetPosition(size) {
      const gap = 28;
      const startX = 40;
      const startY = 40;
      const maxWidth = Math.max(320, window.innerWidth - this.panelWidth - 100);

      let x = startX;
      let y = startY;
      let rowHeight = 0;

      const activeWidgets = this.widgets
        .filter((f) => f.show !== false && f.pageId == this.pageId)
        .slice()
        .sort((a, b) => a.id - b.id);

      activeWidgets.forEach((w) => {
        const ww = (w.size && w.size.w) || 180;
        const wh = (w.size && w.size.h) || 160;

        if (x + ww + gap + size.w > maxWidth) {
          x = startX;
          y += rowHeight + gap;
          rowHeight = 0;
        }

        x += ww + gap;
        rowHeight = Math.max(rowHeight, wh);
      });

      if (x + size.w > maxWidth) {
        x = startX;
        y += rowHeight + gap;
      }

      return { x, y };
    },

    addWidget(wname) {
      const sorted = this.widgets.slice().sort((a, b) => a.id - b.id);
      const newWidgetId = sorted.length === 0 ? 1 : sorted[sorted.length - 1].id + 1;

      const baseMap = {
        ToggleSwitch: {
          w: 180, h: 160, minw: 160, minh: 140, params: { Color: "#008000" },
        },
        Lamp: {
          w: 180, h: 160, minw: 160, minh: 140, params: { Load: "Lamp", Color: "#008000" },
        },
        Step: {
          w: 180, h: 160, minw: 160, minh: 140, params: { value: 0, min: 0, max: 100, step: 1, decimal: 0 },
        },
        Gauge: {
          w: 160, h: 160, minw: 140, minh: 140,
          params: { unit: "", min: 0, max: 100, hollow: 70, startAngle: -135, endAngle: 135, Color: "#008000" },
        },
        TextInput: {
          w: 200, h: 200, minw: 200, minh: 200,
        },
        Chart: {
          w: 360, h: 320, minw: 140, minh: 140, params: { Color: "#008000" },
        },
        TimeInput: {
          w: 180, h: 160, minw: 160, minh: 140,
        },
        ValueDisplay: {
          w: 180, h: 160, minw: 160, minh: 140,
        },
        SilderHorizontal: {
          w: 180, h: 160, minw: 160, minh: 140,
        },
      };

      const base = baseMap[wname] || {
        w: 180, h: 160, minw: 160, minh: 140,
      };

      const pos = this.nextWidgetPosition({ w: base.w, h: base.h });
      const firstTopic = this.$store.state.channel[0]
        ? this.$store.state.channel[0].channel
        : "";

      this.widgets.push({
        id: newWidgetId,
        name: wname,
        pageId: this.pageId,
        payload: "",
        show: true,
        size: {
          w: base.w,
          h: base.h,
          x: pos.x,
          y: pos.y,
          minw: base.minw,
          minh: base.minh,
        },
        timestamp: null,
        topic: firstTopic,
        type: wname,
        deviceType: this.widgettype.find((f) => f.name == wname)?.deviceType,
        ...(base.params
          ? { params: JSON.parse(JSON.stringify(base.params)) }
          : {}),
      });
    },
  },
};
</script>

<style scoped>
.dashboard-stage {
  position: relative;
  overflow: hidden;
}

.dashboard-stage--mobile {
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 14px;
}

.mobile-widget-list {
  padding: 12px;
}

.mobile-widget-card {
  border-radius: 18px !important;
  overflow: hidden;
  margin-bottom: 12px;
  padding: 10px 10px 12px;
}

.mobile-widget-card__header {
  margin-bottom: 8px;
}

.mobile-widget-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #342d42;
}

.mobile-widget-card__subtitle {
  font-size: 11px;
  color: #7c738a;
  margin-top: 2px;
}

.mobile-widget-card__content {
  overflow: hidden;
}

.orbit-panel {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 220px;
  height: 440px;
  overflow: visible;
  pointer-events: auto;
  border-radius: 380px 0 0 380px;
  background:
    radial-gradient(
      circle at 18% 50%,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(210, 185, 245, 0.74) 24%,
      rgba(212, 188, 248, 0.34) 45%,
      rgba(171, 129, 228, 0.18) 60%,
      rgba(120, 83, 186, 0.12) 100%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0.06)
    );
  border-left: 1px solid rgba(234, 3, 255, 0.45);
  box-shadow:
    -24px 0 60px rgba(129, 95, 188, 0.16),
    inset 0 0 60px rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(18px);
  z-index: 99990;
}

.orbit-glow {
  position: absolute;
  inset: 42px 22px 42px 72px;
  border-radius: 999px 0 0 999px;
  background: radial-gradient(
    circle at 30% 50%,
    rgba(255, 255, 255, 0.35),
    rgba(198, 163, 246, 0.08) 55%,
    transparent 75%
  );
  pointer-events: none;
}

.orbit-hover-tooltip {
  position: absolute;
  padding: 10px 14px;
  border-radius: 14px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.94);
  color: #6f42c1;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  box-shadow:
    0 20px 44px rgba(115, 84, 177, 0.3),
    0 0 0 1px rgba(183, 153, 238, 0.38),
    0 0 22px rgba(191, 160, 245, 0.3);
  backdrop-filter: blur(14px);
  pointer-events: none;
  transform-origin: right center;
  z-index: 2147483647;
  will-change: left, top, transform, opacity;
}

.orbit-hover-tooltip::after {
  content: "";
  position: absolute;
  right: -6px;
  top: 50%;
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.94);
  transform: translateY(-50%) rotate(45deg);
  box-shadow: 1px -1px 0 rgba(183, 153, 238, 0.2);
}

.tooltip-pop-enter-active,
.tooltip-pop-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.tooltip-pop-enter,
.tooltip-pop-leave-to {
  opacity: 0;
  transform: translate(-100%, -50%) scale(0.72);
}

.tooltip-pop-enter-to,
.tooltip-pop-leave {
  opacity: 1;
  transform: translate(-100%, -50%) scale(1);
}

.orbit-item {
  position: absolute;
  width: 76px;
  height: 76px;
  border: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7f58ba;
  background: rgba(255, 255, 255, 0.46);
  box-shadow:
    0 10px 28px rgba(115, 84, 177, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.34);
  backdrop-filter: blur(10px);
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease,
    opacity 0.01s ease;
  cursor: pointer;
}

.orbit-item--active,
.orbit-item:hover {
  background: rgba(255, 255, 255, 0.82);
  box-shadow:
    0 16px 36px rgba(115, 84, 177, 0.28),
    0 0 0 4px rgba(178, 151, 234, 0.2);
  color: #6f42c1;
}

.orbit-fade-enter-active,
.orbit-fade-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.orbit-fade-enter,
.orbit-fade-leave-to {
  opacity: 0;
}
</style>